import { useApolloClient } from '@apollo/client'

import {
  FinanceFactSummariesDocument,
  FinancialWorkspaceValueObjectsCostTypeEnum,
  FinancialWorkspaceValueObjectsFinanceTypeEnum,
  FinancialWorkspaceValueObjectsInsightTableTypeEnum,
  InsightTableItemsDocument,
  InsightTableItemsQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import {
  InsightTableGroupBy,
  InsightTableType,
} from 'modules/finance/components/FinancialWorkspace/types/table'
import { FinancialWorkspaceRecord, ChildrenPaginationInfo } from 'modules/finance/components/FinancialWorkspaceDataCollection/types'
import { usePermissionsData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/PermissionsProvider'
import { useSourcesVisibility } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/hooks/useSourcesVisibility'
import { getFinanceTypes } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/lib/sources'

import { resolveItemsPerPage } from 'modules/finance/components/FinancialWorkspace/components/Table/const'
import {
  mapFinanceFactSummariesData,
  mapInsightTableItemsData,
} from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/mapQueryData'
import { buildComparisonDataMap } from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/transformRecords'
import {
  shouldShowExpanderForFinanceSummaries,
  shouldShowExpanderForInsightTable,
} from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/expanderHelpers'
import { useFetchChildrenForFinanceFactSummaries } from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/useFetchChildren'
import { useGraphqlMultiDataCollectionSource } from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/useGraphqlMultiDataCollectionSource'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'
import { DEFAULT_CURRENCY } from 'modules/finance/components/FinancialWorkspace/components/Table/const'

// Cost type for the query
// - EmployeeCosts uses 'labor_cost' to show labor cost calculations
// - Payroll and Compensation use 'normal' for standard values
const COST_TYPE_LABOR: FinancialWorkspaceValueObjectsCostTypeEnum = 'labor_cost'
const COST_TYPE_NORMAL: FinancialWorkspaceValueObjectsCostTypeEnum = 'normal'

interface UseDataCollectionSourceOptions {
  search?: string
  type: InsightTableType
  groupBy: InsightTableGroupBy
  headers: string[]
  isGrouped?: boolean
}

/**
 * Main data source hook for the DataCollectionTable
 *
 * Routes between two different query strategies:
 * - Payroll: Uses InsightTableItemsDocument
 * - EmployeeCosts/Compensation: Uses FinanceFactSummariesDocument
 *
 * Supports lazy loading of children for EmployeeCosts when rows are expanded.
 */
const buildSummariesDefinition = (headers: string[]): Record<string, { type: 'sum' }> => {
  const definition: Record<string, { type: 'sum' }> = {}
  for (const header of headers) {
    definition[header] = { type: 'sum' }
  }
  return definition
}

const computeSummaryRecord = (
  records: FinancialWorkspaceRecord[],
  hasComparisonDate: boolean,
  formatAmount: (amountInCents: string | undefined) => string
): FinancialWorkspaceRecord | undefined => {
  const outermostRows = records.filter((r) => r.level === 0)

  if (outermostRows.length === 0) {
    return undefined
  }

  const periodSums: Record<string, number> = {}
  const comparisonSums: Record<string, number> = {}

  for (const row of outermostRows) {
    for (const pv of row.periodValues ?? []) {
      periodSums[pv.label] = (periodSums[pv.label] ?? 0) + (Number(pv.amountInCents) || 0)
    }
    if (hasComparisonDate) {
      for (const cv of row.comparisonValues ?? []) {
        comparisonSums[cv.label] = (comparisonSums[cv.label] ?? 0) + (Number(cv.amountInCents) || 0)
      }
    }
  }

  const summaryRecord: FinancialWorkspaceRecord = {
    id: '__summary__',
    level: 0,
    label: null,
    hasChildren: false,
    periodValues: Object.entries(periodSums).map(([label, sum]) => ({
      label,
      amountInCents: String(sum),
    })),
    ...(hasComparisonDate
      ? {
          comparisonValues: Object.entries(comparisonSums).map(([label, sum]) => ({
            label,
            amountInCents: String(sum),
          })),
        }
      : {}),
  }

  // Set each period label as a direct key so f0 can access summaryData[column.summary]
  for (const pv of summaryRecord.periodValues) {
    summaryRecord[pv.label] = formatAmount(pv.amountInCents)
  }
  for (const cv of summaryRecord.comparisonValues ?? []) {
    summaryRecord[cv.label] = formatAmount(cv.amountInCents)
  }

  return summaryRecord
}

export const useDataCollectionSource = ({
  type,
  search,
  groupBy,
  headers,
  isGrouped = false
}: UseDataCollectionSourceOptions) => {
  const client = useApolloClient()
  const { formattedState, supportedCurrencies } = useFinancialWorkspace()
  const { company } = useCurrentSession()
  const {
    permissions: { canSeeCompensations },
  } = usePermissionsData()

  const currency = supportedCurrencies.includes(String(company.currency))
    ? company.currency
    : DEFAULT_CURRENCY
  const locale = company.locale ?? 'en'

  const formatAmount = (amountInCents: string | undefined): string => {
    const amount = Number(amountInCents) || 0
    if (!isCurrency(String(currency))) return '-'
    return MoneyWithCurrency({
      amount,
      currency: String(currency),
      locale,
      optionalDecimals: false,
    })
  }

  const visibility = useSourcesVisibility()
  const financeTypes = getFinanceTypes(visibility)

  const itemsPerPage = resolveItemsPerPage(type, groupBy)

  const useFinanceFactSummaries =
    type === InsightTableType.EmployeeCosts ||
    (type === InsightTableType.Compensation && canSeeCompensations)

  const effectiveFinanceTypes = useUnsafeMemo<FinancialWorkspaceValueObjectsFinanceTypeEnum[]>(
    () => {
      if (type === InsightTableType.EmployeeCosts) {
        return financeTypes
      }

      if (type === InsightTableType.Compensation) {
        return ['compensations']
      }

      return []
    },
    [type, financeTypes]
  )

  const effectiveCostType =
    type === InsightTableType.Compensation || type === InsightTableType.Payroll
      ? COST_TYPE_NORMAL
      : COST_TYPE_LABOR

  // Serialize filter arrays for stable dependency tracking
  const legalEntityIdsKey = JSON.stringify(formattedState.legalEntityIds ?? [])
  const locationIdsKey = JSON.stringify(formattedState.locationIds ?? [])
  const costCenterIdsKey = JSON.stringify(formattedState.costCenterIds ?? [])
  const employeeIdsKey = JSON.stringify(formattedState.employeeIds ?? [])
  const conceptIdsKey = JSON.stringify(formattedState.conceptIds ?? [])
  const referenceContractTreeLevelUuidsKey = JSON.stringify(
    formattedState.referenceContractTreeLevelUuids ?? []
  )
  const financeTypesKey = JSON.stringify(effectiveFinanceTypes)

  const formattedStartDate = formattedState.formattedStartDate
  const formattedEndDate = formattedState.formattedEndDate
  const formattedCompareToStartDate = formattedState.formattedCompareToStartDate
  const formattedCompareToEndDate = formattedState.formattedCompareToEndDate

  const hasComparisonDate = !!(formattedCompareToStartDate && formattedCompareToEndDate)

  const fetchChildrenForFinanceFactSummaries = useFetchChildrenForFinanceFactSummaries({
    client,
    search,
    groupBy,
    locationIdsKey,
    employeeIdsKey,
    financeTypesKey,
    formattedEndDate,
    costCenterIdsKey,
    hasComparisonDate,
    legalEntityIdsKey,
    effectiveCostType,
    formattedStartDate,
    formattedCompareToEndDate,
    formattedCompareToStartDate,
    referenceContractTreeLevelUuidsKey
  })

  const sources = useUnsafeMemo(() => {
    const baseSource = {
      id: 'financeFactSummaries',
      document: FinanceFactSummariesDocument,
      fetchPolicy: 'network-only' as const,
      mapVariables: () => ({
        itemsPerPage,
        managerIds: [],
        groupBy: groupBy,
        customCategoryIds: [],
        excludeChildrenData: true,
        search: search ?? undefined,
        costType: effectiveCostType,
        financeTypes: effectiveFinanceTypes,
        conceptIds: formattedState.conceptIds ?? [],
        locationIds: formattedState.locationIds ?? [],
        employeeIds: formattedState.employeeIds ?? [],
        formattedEndDate: formattedState.formattedEndDate,
        costCenterIds: formattedState.costCenterIds ?? [],
        legalEntityIds: formattedState.legalEntityIds ?? [],
        formattedStartDate: formattedState.formattedStartDate,
        referenceContractTreeLevelUuids: formattedState.referenceContractTreeLevelUuids ?? []
      }),
    }

    if (!hasComparisonDate) {
      return [baseSource] as const
    }

    return [
      baseSource,
      {
        id: 'comparisonFinanceFactSummaries',
        document: FinanceFactSummariesDocument,
        fetchPolicy: 'network-only' as const,
        mapVariables: () => ({
          itemsPerPage,
          managerIds: [],
          groupBy: groupBy,
          customCategoryIds: [],
          excludeChildrenData: true,
          search: search ?? undefined,
          costType: effectiveCostType,
          financeTypes: effectiveFinanceTypes,
          conceptIds: formattedState.conceptIds ?? [],
          locationIds: formattedState.locationIds ?? [],
          employeeIds: formattedState.employeeIds ?? [],
          costCenterIds: formattedState.costCenterIds ?? [],
          legalEntityIds: formattedState.legalEntityIds ?? [],
          formattedEndDate: formattedState.formattedCompareToEndDate,
          formattedStartDate: formattedState.formattedCompareToStartDate,
          referenceContractTreeLevelUuids: formattedState.referenceContractTreeLevelUuids ?? []
        }),
      },
    ] as const
  }, [
    search,
    groupBy,
    itemsPerPage,
    formattedState,
    effectiveCostType,
    effectiveFinanceTypes,
    hasComparisonDate,
  ])

  const summariesDefinition = useUnsafeMemo(
    () => isGrouped ? buildSummariesDefinition(headers) : undefined,
    [headers, isGrouped]
  )

  const financeFactSummariesSources = useGraphqlMultiDataCollectionSource({
    paginationType: 'infinite-scroll' as const,
    perPage: itemsPerPage,

    search: { enabled: false },
    sources,

    ...(summariesDefinition ? { summaries: summariesDefinition } : {}),

    mapData: ({ sourcesData }) => {
      const financeData = sourcesData.financeFactSummaries

      if (!financeData) {
        return []
      }

      const comparisonFinanceData = hasComparisonDate
        ? sourcesData.comparisonFinanceFactSummaries
        : undefined

      const comparisonData = buildComparisonDataMap(comparisonFinanceData, 0)

      return mapFinanceFactSummariesData(0, financeData, groupBy, comparisonData)
    },

    mapSummaries: ({ sourcesData }) => {
      if (!isGrouped) {
        return undefined
      }

      const financeData = sourcesData.financeFactSummaries

      if (!financeData) {
        return undefined
      }

      const comparisonFinanceData = hasComparisonDate
        ? sourcesData.comparisonFinanceFactSummaries
        : undefined

      const comparisonData = buildComparisonDataMap(comparisonFinanceData, 0)
      const records = mapFinanceFactSummariesData(0, financeData, groupBy, comparisonData)

      return computeSummaryRecord(records, hasComparisonDate, formatAmount)
    },

    mapPagination: ({ sourcesData }) => {
      const financeData = sourcesData.financeFactSummaries

      if (!financeData) {
        return {
          total: 0,
          cursors: {
            financeFactSummaries: null,
            ...(hasComparisonDate
              ? {
                  comparisonFinanceFactSummaries: null,
                }
              : {}),
          },
          hasNextPageBySource: {
            financeFactSummaries: false,
            ...(hasComparisonDate
              ? {
                  comparisonFinanceFactSummaries: false,
                }
              : {}),
          },
        }
      }

      const connection = financeData.financialWorkspace.financeFactSummariesConnection
      const comparisonData = sourcesData.comparisonFinanceFactSummaries
      const comparisonConnection = comparisonData?.financialWorkspace.financeFactSummariesConnection

      return {
        total: connection.totalCount ?? 0,
        cursors: {
          financeFactSummaries: connection.pageInfo.endCursor ?? null,
          ...(hasComparisonDate
            ? {
                comparisonFinanceFactSummaries: comparisonConnection?.pageInfo.endCursor ?? null,
              }
            : {}),
        },
        hasNextPageBySource: {
          financeFactSummaries: connection.pageInfo.hasNextPage ?? false,
          ...(hasComparisonDate
            ? {
                comparisonFinanceFactSummaries: Boolean(comparisonConnection?.pageInfo.hasNextPage),
              }
            : {}),
        },
      }
    },

    itemsWithChildren: (item: FinancialWorkspaceRecord) =>
      shouldShowExpanderForFinanceSummaries(groupBy, item),

    fetchChildren: async ({
      item,
      pagination,
    }: {
      item: FinancialWorkspaceRecord
      pagination?: ChildrenPaginationInfo
    }) => {
      const { children, paginationInfo } = await fetchChildrenForFinanceFactSummaries(
        item,
        pagination
      )
      return {
        paginationInfo,
        records: children,
        type: 'basic' as const,
      }
    },
  }, [
    formattedState.formattedStartDate,
    formattedState.formattedEndDate,
    formattedState.referenceContractTreeLevelUuids,
    legalEntityIdsKey,
    locationIdsKey,
    costCenterIdsKey,
    employeeIdsKey,
    conceptIdsKey,
    groupBy,
    search,
    itemsPerPage,
    financeTypesKey,
    fetchChildrenForFinanceFactSummaries
  ])

  // Use InsightTableItems for Payroll
  const insightTableType: FinancialWorkspaceValueObjectsInsightTableTypeEnum = 'payroll'

  const insightTableItemsSource = useGraphqlDataCollectionSource(
    {
      document: InsightTableItemsDocument,
      fetchPolicy: 'network-only',
      paginationType: 'infinite-scroll' as const,
      perPage: itemsPerPage,

      search: { enabled: false },

      ...(summariesDefinition ? { summaries: summariesDefinition } : {}),

      mapData: (data: InsightTableItemsQuery) =>
        mapInsightTableItemsData(0, data, groupBy),

      mapSummaries: (data: InsightTableItemsQuery) => {
        if (!isGrouped) {
          return undefined
        }
        const records = mapInsightTableItemsData(0, data, groupBy)
        return computeSummaryRecord(records, false, formatAmount)
      },

      mapVariables: () => ({
        resourceFilter: {
          formattedStartDate: formattedState.formattedStartDate,
          formattedEndDate: formattedState.formattedEndDate,
          groupBy: groupBy,
          legalEntityIds: formattedState.legalEntityIds ?? [],
          locationIds: formattedState.locationIds ?? [],
          costCenterIds: formattedState.costCenterIds ?? [],
          employeeIds: formattedState.employeeIds ?? [],
          conceptIds: formattedState.conceptIds ?? [],
          referenceContractTreeLevelUuids: formattedState.referenceContractTreeLevelUuids ?? [],
          search: search ?? undefined,
          insightTableType,
        },
        itemsPerPage,
      }),

      mapPagination: (data: InsightTableItemsQuery) => {
        const connection = data.financialWorkspace.insightTableItemsConnection
        return {
          total: connection.totalCount ?? 0,
          cursor: connection.pageInfo.endCursor ?? '0',
          hasNextPage: connection.pageInfo.hasNextPage ?? false,
        }
      },

      // For Payroll, children are always pre-loaded in the response
      itemsWithChildren: shouldShowExpanderForInsightTable,

      fetchChildren: ({ item }: { item: FinancialWorkspaceRecord }) => {
        // Children are pre-loaded for InsightTableItems
        return Promise.resolve({
          records: item.children ?? [],
          type: 'basic' as const,
        })
      },
    },
    [
      formattedState.formattedStartDate,
      formattedState.formattedEndDate,
      legalEntityIdsKey,
      locationIdsKey,
      costCenterIdsKey,
      employeeIdsKey,
      conceptIdsKey,
      referenceContractTreeLevelUuidsKey,
      groupBy,
      search,
      itemsPerPage
    ]
  )

  return useFinanceFactSummaries ? financeFactSummariesSources : insightTableItemsSource
}
